// @ts-nocheck
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useAnalysis } from '../contexts/AnalysisContext';
import { Sun, Moon, LogOut, Shield, TrendingUp, Layers, Search, Brain, Menu, X, Sparkles, Trophy, User as UserIcon, ChevronDown, ArrowLeftRight } from 'lucide-react';
import Footer from './Footer';
import FloatingProgressIndicator from './FloatingProgressIndicator';
import NotificationBell from './NotificationBell';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { isAnalyzing, progress, currentStage, symbol } = useAnalysis();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-zinc-950 text-zinc-50' : 'bg-gray-50 text-gray-900'}`}>
      {/* Top Navigation Bar - Modern Glass Design */}
      <nav className={`sticky top-0 z-50 backdrop-blur transition-colors duration-200 ${
        isDark
          ? 'supports-[backdrop-filter]:bg-black/30 bg-black/20 border-b border-white/10'
          : 'bg-white/95 border-b border-gray-200 shadow-sm'
      }`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo and Brand */}
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
                <img src="/investverse-logo.svg" alt="InvestVerse.AI" className="h-8 w-auto" />
              </Link>

              {/* Navigation Links */}
              {user && (
                <div className="hidden lg:flex items-center gap-2">
                  <Link
                    to="/agent-analysis"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/agent-analysis')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <Brain className="h-4 w-4 text-purple-500 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                    AI Agents
                  </Link>
                  <Link
                    to="/overview"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/overview')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <TrendingUp className="h-4 w-4 text-blue-500 group-hover:scale-110 group-hover:translate-y-[-2px] transition-transform duration-300" />
                    Market Overview
                  </Link>
                  <Link
                    to="/strategy-advisor"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/strategy-advisor')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <Sparkles className="h-4 w-4 text-indigo-500 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                    Strategy Advisor
                  </Link>
                  <Link
                    to="/explore"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/explore')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <Layers className="h-4 w-4 text-emerald-500 group-hover:scale-110 group-hover:rotate-[-12deg] transition-transform duration-300" />
                    Trading Strategies
                  </Link>
                  <Link
                    to="/pairs-analysis"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/pairs-analysis')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <ArrowLeftRight className="h-4 w-4 text-orange-500 group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300" />
                    Pairs Analysis
                  </Link>
                  <Link
                    to="/search"
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/search')
                        ? isDark
                          ? 'bg-white/10 text-white'
                          : 'bg-blue-50 text-blue-700'
                        : isDark
                          ? 'text-white/70 hover:text-white hover:bg-white/5'
                          : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <Search className="h-4 w-4 text-orange-500 group-hover:scale-125 transition-transform duration-300" />
                    Coin Search
                  </Link>
                  <Link
                    to="/contest"
                    className={`relative px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-1.5 group ${
                      isActive('/contest') || isActive('/contest/submit')
                        ? isDark
                          ? 'bg-gradient-to-r from-[#00ffe7]/20 to-purple-500/20 text-[#00ffe7] border border-[#00ffe7]/40 shadow-lg shadow-[#00ffe7]/10'
                          : 'bg-gradient-to-r from-[#00ffe7]/10 to-purple-500/10 text-[#00ffe7] border border-[#00ffe7]/40'
                        : isDark
                          ? 'text-white/70 hover:text-[#00ffe7] hover:bg-gradient-to-r hover:from-[#00ffe7]/10 hover:to-purple-500/10 border border-transparent hover:border-[#00ffe7]/30 hover:shadow-lg hover:shadow-[#00ffe7]/10'
                          : 'text-gray-700 hover:text-[#00ffe7] hover:bg-gradient-to-r hover:from-[#00ffe7]/5 hover:to-purple-500/5 border border-transparent hover:border-[#00ffe7]/30'
                    }`}
                  >
                    <Trophy className="h-4 w-4 text-[#00ffe7] transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 group-hover:animate-trophy-bounce flex-shrink-0" />
                    <span>Contest</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Right Side - Utility Actions */}
            {user && (
              <div className="flex items-center gap-2">
                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className={`hidden sm:flex p-2 rounded-lg transition-all duration-200 ${
                    isDark
                      ? 'hover:bg-white/10 text-white/70 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                  aria-label="Toggle theme"
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>

                {/* Notification Bell */}
                <div className="hidden sm:block">
                  <NotificationBell />
                </div>

                {/* User Menu Dropdown - Desktop */}
                <div className="hidden sm:block relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                      isDark
                        ? 'hover:bg-white/10 text-white/90'
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-white/10' : 'bg-gray-200'
                    }`}>
                      <UserIcon className="h-4 w-4" />
                    </div>
                    <div className="hidden md:block text-left text-sm">
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.email.split('@')[0]}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {user.subscription_tier}
                      </div>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setUserMenuOpen(false)}
                      />
                      <div className={`absolute right-0 mt-2 w-56 rounded-xl shadow-lg z-50 border ${
                        isDark
                          ? 'bg-zinc-900 border-white/10'
                          : 'bg-white border-gray-200'
                      }`}>
                        <div className="p-3">
                          <div className={`px-3 py-2 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {user.email}
                            </div>
                            <div className={`text-xs mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                              {user.subscription_tier}
                            </div>
                          </div>
                          <div className={`my-2 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />
                          {user.is_admin && (
                            <Link
                              to="/admin"
                              onClick={() => setUserMenuOpen(false)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                isDark
                                  ? 'text-white/70 hover:bg-white/10 hover:text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <Shield className="h-4 w-4" />
                              Admin Dashboard
                            </Link>
                          )}
                          <button
                            onClick={() => {
                              setUserMenuOpen(false);
                              logout();
                            }}
                            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                              isDark
                                ? 'text-red-400 hover:bg-red-500/10'
                                : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <LogOut className="h-4 w-4" />
                            Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className={`sm:hidden p-2 rounded-lg transition-all duration-200 ${
                    isDark
                      ? 'hover:bg-white/10 text-white/90'
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                  aria-label="Toggle mobile menu"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {user && mobileMenuOpen && (
          <div className={`md:hidden border-t ${isDark ? 'border-white/10 bg-black/40' : 'border-gray-200 bg-white'}`}>
            <div className="px-4 py-4 space-y-2">
              <Link
                to="/agent-analysis"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/agent-analysis')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Brain className="h-5 w-5 text-purple-500" />
                AI Agents
              </Link>
              <Link
                to="/overview"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/overview')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <TrendingUp className="h-5 w-5 text-blue-500" />
                Market Overview
              </Link>
              <Link
                to="/strategy-advisor"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/strategy-advisor')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Sparkles className="h-5 w-5 text-indigo-500" />
                Strategy Advisor
              </Link>
              <Link
                to="/explore"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/explore')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Layers className="h-5 w-5 text-emerald-500" />
                Trading Strategies
              </Link>
              <Link
                to="/pairs-analysis"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/pairs-analysis')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <ArrowLeftRight className="h-5 w-5 text-orange-500" />
                Pairs Analysis
              </Link>
              <Link
                to="/search"
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/search')
                    ? isDark
                      ? 'bg-white/10 text-white'
                      : 'bg-blue-50 text-blue-700'
                    : isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <Search className="h-5 w-5 text-orange-500" />
                Coin Search
              </Link>
              <Link
                to="/contest"
                onClick={() => setMobileMenuOpen(false)}
                className={`group flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                  isActive('/contest') || isActive('/contest/submit')
                    ? isDark
                      ? 'bg-gradient-to-r from-[#00ffe7]/20 to-purple-500/20 text-[#00ffe7] border border-[#00ffe7]/30'
                      : 'bg-gradient-to-r from-[#00ffe7]/10 to-purple-500/10 text-[#00ffe7] border border-[#00ffe7]/30'
                    : isDark
                      ? 'text-white/70 hover:text-[#00ffe7] hover:bg-gradient-to-r hover:from-[#00ffe7]/10 hover:to-purple-500/10'
                      : 'text-gray-700 hover:text-[#00ffe7] hover:bg-gradient-to-r hover:from-[#00ffe7]/5 hover:to-purple-500/5'
                }`}
              >
                <Trophy className="h-5 w-5 text-[#00ffe7] transition-all duration-300 group-hover:scale-110 group-hover:rotate-12 group-hover:animate-trophy-bounce flex-shrink-0" />
                Contest
              </Link>
              {user.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isActive('/admin')
                      ? isDark
                        ? 'bg-white/10 text-purple-400'
                        : 'bg-purple-50 text-purple-700'
                      : isDark
                        ? 'text-white/70 hover:text-white hover:bg-white/5'
                        : 'text-gray-700 hover:text-purple-600 hover:bg-purple-50'
                  }`}
                >
                  <Shield className="h-5 w-5" />
                  Admin
                </Link>
              )}

              {/* Mobile Settings & User Info */}
              <div className={`pt-4 mt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                {/* Theme Toggle - Mobile */}
                <button
                  onClick={toggleTheme}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
                    isDark
                      ? 'text-white/70 hover:text-white hover:bg-white/5'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  {isDark ? 'Light Mode' : 'Dark Mode'}
                </button>

                <div className={`my-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} />

                {/* User Info */}
                <div className={`px-4 py-2 text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-white/10' : 'bg-gray-200'
                    }`}>
                      <UserIcon className="h-4 w-4" />
                    </div>
                    <div>
                      <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {user.email}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {user.subscription_tier}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    logout();
                  }}
                  className={`w-full mt-2 px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                    isDark
                      ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="transition-colors duration-200">{children}</main>

      {/* Floating Progress Indicator - Global */}
      <FloatingProgressIndicator
        isAnalyzing={isAnalyzing}
        progress={progress}
        currentStage={currentStage}
        symbol={symbol}
        onViewResults={() => {
          // Navigate to agent analysis page and scroll to results
          if (location.pathname !== '/agent-analysis') {
            navigate('/agent-analysis');
          }
          // Scroll after a short delay to allow page load
          setTimeout(() => {
            const resultsSection = document.getElementById('results-section');
            if (resultsSection) {
              resultsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;
